/**
 * Avatar URL Helper Functions
 * Constructs proper avatar URLs for the backend storage structure
 */

/**
 * Get the full avatar URL from filename
 * @param filename - The avatar filename (e.g., "abc123.jpg")
 * @param size - 'thumb' for thumbnail (200px) or 'full' for original size
 * @returns Full avatar URL or undefined if no filename provided
 *
 * @example
 * ```ts
 * getAvatarUrl('abc123.jpg', 'thumb')
 * // Returns: "https://192.168.1.20:3443/public/avatar/thumb/abc123.jpg"
 * ```
 */
export function getAvatarUrl(
  filename?: string | null,
  size: 'thumb' | 'full' = 'thumb',
): string | undefined {
  if (!filename) return undefined;

  const baseUrl = import.meta.env.VITE_HEALTHY_EMPIRE_SERVER;

  if (!baseUrl) {
    console.warn('VITE_HEALTHY_EMPIRE_SERVER is not defined');
    return undefined;
  }

  return `${baseUrl}/public/avatar/${size}/${filename}`;
}

/**
 * Get thumbnail avatar URL (200px, optimized for lists)
 * @param filename - The avatar filename
 * @returns Thumbnail avatar URL or undefined
 */
export function getAvatarThumbUrl(filename?: string | null): string | undefined {
  return getAvatarUrl(filename, 'thumb');
}

/**
 * Get full-size avatar URL (original size, for modals/detailed views)
 * @param filename - The avatar filename
 * @returns Full-size avatar URL or undefined
 */
export function getAvatarFullUrl(filename?: string | null): string | undefined {
  return getAvatarUrl(filename, 'full');
}

/**
 * Extract initials from name for avatar fallback
 * @param name - User's full name
 * @param maxChars - Maximum number of characters to return (default: 2)
 * @returns Initials in uppercase
 *
 * @example
 * ```ts
 * getInitials('Ahmed Ali')      // Returns: "AA"
 * getInitials('John Doe Smith') // Returns: "JD"
 * getInitials('X')               // Returns: "X"
 * getInitials('')                // Returns: "U"
 * ```
 */
export function getInitials(name?: string | null, maxChars: number = 2): string {
  if (!name || name.trim().length === 0) return 'U';

  const words = name.trim().split(/\s+/);

  if (words.length === 1) {
    // Single word: return first character
    return words[0].charAt(0).toUpperCase();
  }

  // Multiple words: take first character of first N words
  return words
    .slice(0, maxChars)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}
